import styled from "@emotion/styled";
import { theme } from "../../lib/theme";
import { media } from "../../lib/breakpoints";
import type { TxFilterStatsResponse } from "../../lib/api";
import { StatsRow, CompactStatCard, StatValue, StatLabel, SubStat } from "../ui";
import { Sparkline } from "../Sparkline";
import { formatAgo, formatFJ, formatFJCompact } from "../../lib/format";

/**
 * Aggregate stats of the current search result: how much of the network the
 * filters select, what the selected transactions pay, when they were active,
 * and where they are in their lifecycle. Always rendered — with no filters it
 * simply describes the whole network.
 */

const Row = styled(StatsRow)`
  margin-bottom: ${theme.spacing.md};

  /* Four-across at phone widths crams the compound substats ("100.0% of
     36,292") into three ragged monospace lines each; 2x2 gives every card a
     full line's worth of width, and keeps the layout symmetric at 320px,
     where flex used to wrap the fourth card into a lone full-width banner. */
  ${media.sm} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

/** The activity card's 30-day trend, tucked between the value and its substat. */
const SparkSlot = styled.div`
  margin: 2px 0;
`;

/** Counts inside substats: one line on a phone card beats exact digits. */
const compact = (n: number): string => {
  if (n < 10_000) return n.toLocaleString();
  const tiers: Array<[number, string]> = [
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  const [div, suffix] = tiers.find(([d]) => n >= d)!;
  return `${(n / div).toFixed(1).replace(/\.0$/, "")}${suffix}`;
};

export interface ResultStatsRowProps {
  stats: TxFilterStatsResponse | undefined;
}

export function ResultStatsRow({ stats }: ResultStatsRowProps) {
  const topStatuses = stats?.statuses.slice(0, 3) ?? [];

  return (
    <Row>
      <CompactStatCard>
        <StatLabel>Matching txs</StatLabel>
        <StatValue>{stats ? stats.matching.toLocaleString() : "-"}</StatValue>
        {stats && (
          <SubStat>
            {stats.pct.toFixed(1)}% of {compact(stats.networkTotal)}
          </SubStat>
        )}
      </CompactStatCard>
      {/* Median leads (a few outliers drag the mean); the mean and the full
          range ride underneath, since the gap between them is the signal. */}
      <CompactStatCard>
        <StatLabel>Median fee</StatLabel>
        <StatValue style={{ color: theme.colors.accent }}>
          {stats ? formatFJ(stats.fee.median) : "-"}
        </StatValue>
        {stats && stats.fee.min !== null && (
          <SubStat>
            mean {formatFJCompact(Number(stats.fee.mean))} · range{" "}
            {formatFJCompact(Number(stats.fee.min))}–{formatFJCompact(Number(stats.fee.max))} FJ
          </SubStat>
        )}
      </CompactStatCard>
      <CompactStatCard>
        <StatLabel>Last 24h</StatLabel>
        <StatValue style={{ color: theme.colors.success }}>
          {stats ? `${compact(stats.activity.last24h)} txs` : "-"}
        </StatValue>
        {stats && (
          <>
            <SparkSlot title={`Matching txs per day, last ${stats.activity.daily.length} days`}>
              <Sparkline data={stats.activity.daily} color={theme.colors.success} height={18} />
            </SparkSlot>
            <SubStat>
              {stats.activity.firstSeen && stats.activity.lastSeen
                ? `since ${new Date(stats.activity.firstSeen).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${formatAgo(stats.activity.lastSeen)}`
                : "no matches"}
            </SubStat>
          </>
        )}
      </CompactStatCard>
      <CompactStatCard>
        <StatLabel>Statuses</StatLabel>
        <StatValue style={{ textTransform: "capitalize" }}>
          {topStatuses.length > 0 ? topStatuses[0].status : "-"}
        </StatValue>
        <SubStat>
          {/* The top status is already the value above — its count leads, the
              runners-up follow with names. */}
          {topStatuses.length > 0
            ? [
                compact(topStatuses[0].count),
                ...topStatuses.slice(1).map((s) => `${s.status} ${compact(s.count)}`),
              ].join(" · ")
            : "no matches"}
        </SubStat>
      </CompactStatCard>
    </Row>
  );
}
